<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactConversations from './ContactConversations.vue';
import ContactContracts from './ContactContracts.vue';
import CommercialContextPanel from './CommercialContextPanel.vue';
import AiConversationPanel from './AiConversationPanel.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ContactInfo from './contact/ContactInfo.vue';
import ContactNotes from './contact/ContactNotes.vue';
import ConversationInfo from './ConversationInfo.vue';
import AttendanceProtocolPanel from './AttendanceProtocolPanel.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import SharedFiles from './SharedFiles.vue';
import Draggable from 'vuedraggable';
import MacrosList from './Macros/List.vue';
import ShopifyOrdersList from 'dashboard/components/widgets/conversation/ShopifyOrdersList.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/IssuesList.vue';
import LinearSetupCTA from 'dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { dynamicTime } from 'shared/helpers/timeHelper';
import {
  ContractTemplatesAPI,
  ContractsAPI,
} from 'dashboard/api/talkviaContracts';
import { useAlert } from 'dashboard/composables';
import ContractSendDialog from '../contracts/ContractSendDialog.vue';
import ContractCreateDialog from '../contracts/ContractCreateDialog.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const route = useRoute();
const router = useRouter();
const copy = {
  viewOnKanban: 'Ver no Kanban',
  simulateBanks: 'Simular nos bancos',
  sendContract: 'Enviar contrato',
  contracts: 'Contratos',
  noContractTemplate: 'Cadastre um modelo de contrato antes de enviar.',
  contractSent: 'Contrato gerado e enviado para o cliente.',
  customerCenter: 'Central do cliente',
  unnamedCustomer: 'Cliente sem nome',
  crmSnapshot: 'Contexto rápido',
  protocol: 'Protocolo',
  cpf: 'CPF',
  origin: 'Origem',
  lastActivity: 'Última atividade',
  labels: 'Etiquetas',
  collapseSnapshot: 'Recolher contexto rápido',
  expandSnapshot: 'Mostrar contexto rápido',
  noProtocol: 'Sem protocolo',
  noCpf: 'Sem CPF',
  noOrigin: 'Origem não informada',
  noActivity: 'Sem atividade',
  noLabels: 'Sem etiquetas',
};
const {
  uiSettings,
  updateUISettings,
  isContactSidebarItemOpen,
  conversationSidebarItemsOrder,
  toggleSidebarUIState,
} = useUISettings();

const dragging = ref(false);
const conversationSidebarItems = ref([]);
const contractTemplates = ref([]);
const contractSendDialogRef = ref(null);
const contractCreateDialogRef = ref(null);
const contactInfoRef = ref(null);

const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);

const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled
);

const { isCloudFeatureEnabled } = useAccount();

const isLinearFeatureEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.LINEAR)
);

const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);

const isLinearClientIdConfigured = computed(() => {
  return !!linearIntegration.value?.id;
});

const isLinearConnected = computed(
  () => linearIntegration.value?.enabled || false
);

const hasCorbanModule = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.TALKVIA_CORBAN)
);

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const conversationId = computed(() => props.conversationId);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);
const attendanceProtocol = computed(
  () =>
    currentChat.value?.attendance_protocol ||
    conversationAdditionalAttributes.value.attendance_protocol ||
    ''
);
const attendanceProtocols = computed(
  () =>
    currentChat.value?.attendance_protocols ||
    conversationAdditionalAttributes.value.attendance_protocols ||
    []
);

const channelType = computed(() => currentChat.value.meta?.channel);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

const compactUrl = value => {
  if (!value) return '';

  try {
    return new URL(value).hostname.replace(/^www\./, '');
  } catch {
    return value;
  }
};

const originLabel = computed(() =>
  compactUrl(
    conversationAdditionalAttributes.value.referer ||
      conversationAdditionalAttributes.value.source ||
      contactAdditionalAttributes.value.source ||
      contactAdditionalAttributes.value.referer
  )
);

const lastActivityLabel = computed(() => {
  const timestamp = currentChat.value?.last_activity_at;
  return timestamp ? dynamicTime(timestamp) : '';
});

const conversationLabels = computed(() => currentChat.value?.labels || []);

const crmContextCards = computed(() => [
  {
    key: 'protocol',
    icon: 'i-lucide-fingerprint',
    label: copy.protocol,
    value: attendanceProtocol.value || copy.noProtocol,
    muted: !attendanceProtocol.value,
  },
  {
    key: 'cpf',
    icon: 'i-lucide-id-card',
    label: copy.cpf,
    value: contact.value?.talkvia_profile?.cpf || copy.noCpf,
    muted: !contact.value?.talkvia_profile?.cpf,
  },
  {
    key: 'origin',
    icon: 'i-lucide-route',
    label: copy.origin,
    value: originLabel.value || copy.noOrigin,
    muted: !originLabel.value,
  },
  {
    key: 'activity',
    icon: 'i-lucide-clock-3',
    label: copy.lastActivity,
    value: lastActivityLabel.value || copy.noActivity,
    muted: !lastActivityLabel.value,
  },
]);

const labelPreview = computed(() => {
  if (!conversationLabels.value.length) return copy.noLabels;
  return conversationLabels.value.slice(0, 3).join(', ');
});

const hiddenLabelsCount = computed(() =>
  Math.max(conversationLabels.value.length - 3, 0)
);
const hiddenLabelsLabel = computed(() =>
  hiddenLabelsCount.value ? `+${hiddenLabelsCount.value}` : ''
);

const isCrmContextCollapsed = computed(
  () => !!uiSettings.value.is_contact_quick_context_collapsed
);

const toggleCrmContext = () => {
  updateUISettings({
    is_contact_quick_context_collapsed: !isCrmContextCollapsed.value,
  });
};

const openBankSimulation = () => {
  contactInfoRef.value?.openBankSimulationModal();
};

const openInKanban = () => {
  router.push({
    path: frontendURL(`accounts/${route.params.accountId}/kanban`),
    query: { conversation_id: conversationId.value },
  });
};

const openProtocolHistory = () => {
  router.push({
    name: 'contacts_edit',
    params: {
      accountId: route.params.accountId,
      contactId: contactId.value,
    },
    query: { tab: 'history' },
  });
};

const fetchContractTemplates = async () => {
  const { data } = await ContractTemplatesAPI.get({ active: true });
  contractTemplates.value = data || [];
};

const sendContract = () => {
  if (!contractTemplates.value.length) {
    useAlert(copy.noContractTemplate);
    return;
  }
  contractCreateDialogRef.value?.open(contractTemplates.value, contact.value);
};

const handleCreateContract = async ({
  templateId,
  recipientName,
  recipientCpf,
  recipientPhone,
}) => {
  const { data } = await ContractsAPI.create({
    talkvia_contract_template_id: templateId,
    contact_id: contactId.value,
    conversation_id: conversationId.value,
    recipient_name: recipientName,
    recipient_cpf: recipientCpf,
    recipient_phone: recipientPhone,
    source: 'contact_panel',
  });
  contractSendDialogRef.value?.open(data);
  useAlert('Contrato gerado.');
};

const handleSendContractLink = async ({ contract, payload }) => {
  await ContractsAPI.sendLink(contract.id, payload);
  useAlert(copy.contractSent);
};

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

const onDragEnd = () => {
  dragging.value = false;
  updateUISettings({
    conversation_sidebar_items_order: conversationSidebarItems.value,
  });
};

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

onMounted(() => {
  conversationSidebarItems.value = conversationSidebarItemsOrder.value;
  getContactDetails();
  store.dispatch('attributes/get', 0);
  // Load integrations to ensure linear integration state is available
  store.dispatch('integrations/get', 'linear');
  fetchContractTemplates();
});
</script>

<template>
  <div class="contact-workspace w-full">
    <SidebarActionsHeader
      :title="copy.customerCenter"
      @close="closeContactPanel"
    />
    <div
      class="border-t border-n-weak bg-n-solid-2 px-3 pt-3"
      :class="isCrmContextCollapsed ? 'pb-1' : 'pb-2'"
    >
      <button
        type="button"
        class="flex w-full items-center justify-between rounded-md py-1 text-left text-[10px] font-semibold uppercase tracking-wide text-n-slate-10 transition hover:text-n-slate-12"
        :class="isCrmContextCollapsed ? 'mb-0' : 'mb-1.5'"
        :title="
          isCrmContextCollapsed ? copy.expandSnapshot : copy.collapseSnapshot
        "
        :aria-expanded="!isCrmContextCollapsed"
        @click="toggleCrmContext"
      >
        <span>{{ copy.crmSnapshot }}</span>
        <span
          class="i-lucide-chevron-down size-3.5 transition-transform"
          :class="isCrmContextCollapsed ? '-rotate-90' : 'rotate-0'"
        />
      </button>
      <div v-if="!isCrmContextCollapsed" class="grid gap-1.5">
        <div
          v-for="card in crmContextCards"
          :key="card.key"
          class="contact-workspace__insight"
        >
          <span :class="card.icon" class="size-3.5 text-n-brand" />
          <div class="min-w-0">
            <span class="block text-[10px] font-medium text-n-slate-10">
              {{ card.label }}
            </span>
            <span
              class="block truncate text-[13px] font-semibold leading-5"
              :class="card.muted ? 'text-n-slate-10' : 'text-n-slate-12'"
            >
              {{ card.value }}
            </span>
          </div>
        </div>
        <div class="contact-workspace__insight">
          <span class="i-lucide-tags size-3.5 text-n-brand" />
          <div class="min-w-0">
            <span class="block text-[10px] font-medium text-n-slate-10">
              {{ copy.labels }}
            </span>
            <span
              class="block truncate text-[13px] font-semibold leading-5 text-n-slate-12"
            >
              {{ labelPreview }}
              <span v-if="hiddenLabelsLabel">{{ hiddenLabelsLabel }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <ContactInfo
      ref="contactInfoRef"
      :contact="contact"
      :channel-type="channelType"
      :enable-bank-simulation="hasCorbanModule"
    />
    <CommercialContextPanel
      v-if="hasCorbanModule"
      :contact="contact"
      :conversation-id="conversationId"
    />
    <AiConversationPanel :conversation="currentChat" />
    <div class="px-2 pb-3">
      <div class="grid gap-2">
        <button
          v-if="hasCorbanModule"
          type="button"
          class="contact-workspace__action"
          @click="openBankSimulation"
        >
          <span class="inline-flex items-center gap-2">
            <span class="i-lucide-landmark size-4 text-n-brand" />
            {{ copy.simulateBanks }}
          </span>
          <span class="i-lucide-arrow-up-right size-4 text-n-slate-10" />
        </button>
        <button
          type="button"
          class="contact-workspace__action"
          @click="openInKanban"
        >
          <span class="inline-flex items-center gap-2">
            <span class="i-lucide-kanban-square size-4 text-n-brand" />
            {{ copy.viewOnKanban }}
          </span>
          <span class="i-lucide-arrow-up-right size-4 text-n-slate-10" />
        </button>
        <button
          type="button"
          class="contact-workspace__action"
          @click="sendContract"
        >
          <span class="inline-flex items-center gap-2">
            <span class="i-lucide-file-signature size-4 text-n-brand" />
            {{ copy.sendContract }}
          </span>
          <span class="i-lucide-send size-4 text-n-slate-10" />
        </button>
      </div>
    </div>
    <div class="px-2 pb-8 list-group">
      <Draggable
        :list="conversationSidebarItems"
        animation="200"
        ghost-class="ghost"
        handle=".drag-handle"
        item-key="name"
        class="flex flex-col gap-3"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div
            v-if="element.name === 'conversation_actions'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              @toggle="
                value => toggleSidebarUIState('is_conv_actions_open', value)
              "
            >
              <ConversationAction
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'conversation_participants'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
              :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
              @toggle="
                value =>
                  toggleSidebarUIState('is_conv_participants_open', value)
              "
            >
              <ConversationParticipant
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'conversation_info'"
            class="space-y-3"
          >
            <AccordionItem
              :title="$t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.SIDEBAR_TITLE')"
              :is-open="isContactSidebarItemOpen('is_attendance_protocol_open')"
              compact
              @toggle="
                value =>
                  toggleSidebarUIState('is_attendance_protocol_open', value)
              "
            >
              <AttendanceProtocolPanel
                :conversation-id="conversationId"
                :protocol="attendanceProtocol"
                :protocols="attendanceProtocols"
                @view-full-history="openProtocolHistory"
              />
            </AccordionItem>
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
              :is-open="isContactSidebarItemOpen('is_conv_details_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_conv_details_open', value)
              "
            >
              <ConversationInfo
                :conversation-attributes="conversationAdditionalAttributes"
                :contact-attributes="contactAdditionalAttributes"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_attributes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
              :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
              compact
              @toggle="
                value =>
                  toggleSidebarUIState('is_contact_attributes_open', value)
              "
            >
              <CustomAttributes
                attribute-type="contact_attribute"
                attribute-from="conversation_contact_panel"
                :contact-id="contact.id"
                :empty-state-message="
                  $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
                "
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'previous_conversation'">
            <AccordionItem
              v-if="contact.id"
              :title="
                $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
              "
              :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_previous_conv_open', value)
              "
            >
              <ContactConversations
                :contact-id="contact.id"
                :conversation-id="conversationId"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_contracts'">
            <AccordionItem
              v-if="contact.id"
              :title="copy.contracts"
              :is-open="isContactSidebarItemOpen('is_contact_contracts_open')"
              compact
              @toggle="
                value =>
                  toggleSidebarUIState('is_contact_contracts_open', value)
              "
            >
              <ContactContracts :contact-id="contact.id" />
            </AccordionItem>
          </div>
          <woot-feature-toggle
            v-else-if="element.name === 'macros'"
            feature-key="macros"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
              :is-open="isContactSidebarItemOpen('is_macro_open')"
              compact
              @toggle="value => toggleSidebarUIState('is_macro_open', value)"
            >
              <MacrosList :conversation-id="conversationId" />
            </AccordionItem>
          </woot-feature-toggle>
          <div
            v-else-if="
              element.name === 'linear_issues' &&
              isLinearFeatureEnabled &&
              isLinearClientIdConfigured
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
              :is-open="isContactSidebarItemOpen('is_linear_issues_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_linear_issues_open', value)
              "
            >
              <LinearSetupCTA v-if="!isLinearConnected" />
              <LinearIssuesList v-else :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'shopify_orders' && isShopifyFeatureEnabled
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
              :is-open="isContactSidebarItemOpen('is_shopify_orders_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_shopify_orders_open', value)
              "
            >
              <ShopifyOrdersList :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_notes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES')"
              :is-open="isContactSidebarItemOpen('is_contact_notes_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_contact_notes_open', value)
              "
            >
              <ContactNotes :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'shared_files'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHARED_FILES')"
              :is-open="isContactSidebarItemOpen('is_shared_files_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_shared_files_open', value)
              "
            >
              <SharedFiles />
            </AccordionItem>
          </div>
        </template>
      </Draggable>
    </div>
    <ContractCreateDialog
      ref="contractCreateDialogRef"
      @create="handleCreateContract"
    />
    <ContractSendDialog
      ref="contractSendDialogRef"
      @send="handleSendContractLink"
    />
  </div>
</template>

<style lang="scss" scoped>
.contact-workspace {
  @apply bg-n-solid-2;
}

.contact-workspace__insight {
  @apply grid min-w-0 grid-cols-[2rem_minmax(0,1fr)] items-center gap-2 rounded-xl border border-n-weak bg-n-solid-1 px-2.5 py-2;

  > span:first-child {
    @apply grid size-8 place-items-center rounded-lg bg-n-alpha-2 text-n-brand;
  }
}

.contact-workspace__action {
  @apply flex w-full items-center justify-between rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3 text-left text-sm font-semibold text-n-slate-12 transition hover:border-n-brand hover:bg-n-alpha-2;
}

:deep(.contact--profile) {
  @apply pb-3 border-b border-solid border-n-weak;
}
</style>
